import React from 'react';

import {
  allStringOperatorsExceptIsEmptyAndIsNotEmpty,
  containsIsEmptyIsNotEmpty,
  containsIsEmptyIsNotEmptyIsAnyOf,
  containsOnly,
  isEmptyIsNotEmptyIsAnyOf,
  singleSelectIs,
  singleSelectIsIsAnyOf,
  singleSelectIsIsNotIsAnyOf
} from 'components/MUIDataGrid/filterOperators';
import { getPatientStatusNames } from 'components/pages/patient-source/Patients/constants';

import { dateValueFormatter } from '../../../../constants/dateFormat';

import {
  ADDITIONAL_COMMUNICATION_OPTIONS,
  OPT_IN,
  OPT_OUT,
  PATIENT_LEAD_STATUS_OPTIONS,
  WEBFORM_SOURCE_NAME
} from './constants';
import PatientLeadStatusEditCell from './PatientLeadStatusEditCell';

const formatArrayForGrid = value => {
  if (Array.isArray(value)) {
    return value.length > 0 ? value.join('; ') : '';
  }

  return defaultValueFormatter(value);
};

export const COLUMNS = [
  {
    field: 'patient',
    headerName: 'Patient',
    flex: 1,
    minWidth: 150,
    filterOperators: containsOnly,
    valueGetter: (value, row) => `${row.firstName} ${row.lastName}`,
    valueFormatter: (value, row) => {
      const { firstName, lastName } = row;
      return `${firstName} ${lastName}`;
    }
  },
  {
    field: 'patientId',
    headerName: 'Patient ID',
    flex: 1,
    minWidth: 120,
    filterOperators: containsOnly,
    valueGetter: defaultValueFormatter,
    valueFormatter: defaultValueFormatter
  },
  {
    field: 'age',
    headerName: 'Age',
    flex: 0.5,
    minWidth: 80,
    type: 'number'
  },
  {
    field: 'heightIn',
    headerName: 'Height (in)',
    flex: 0.7,
    minWidth: 100,
    type: 'number'
  },
  {
    field: 'weightLbs',
    headerName: 'Weight (lbs)',
    flex: 0.7,
    minWidth: 120,
    type: 'number'
  },
  {
    field: 'bmi',
    headerName: 'Calculated BMI',
    flex: 0.7,
    minWidth: 130,
    type: 'number'
  },
  {
    field: 'studyName',
    headerName: 'Study',
    flex: 1,
    minWidth: 200,
    valueGetter: (value, row) => (!row.studyId && !row.studyName ? 'General Interest' : row.studyName),
    valueFormatter: (value, row) => {
      const { studyId, studyName } = row;
      return !studyId && !studyName ? 'General Interest' : studyName;
    }
  },
  {
    field: 'pcn',
    headerName: 'PCN',
    flex: 1,
    minWidth: 120,
    filterOperators: isEmptyIsNotEmptyIsAnyOf,
    valueFormatter: defaultValueFormatter
  },
  {
    field: 'therapeuticArea',
    headerName: 'Therapeutic Area',
    flex: 1,
    minWidth: 160,
    filterOperators: containsIsEmptyIsNotEmptyIsAnyOf,
    valueFormatter: defaultValueFormatter
  },
  {
    field: 'indication',
    headerName: 'Indication',
    flex: 1,
    minWidth: 150,
    filterOperators: containsIsEmptyIsNotEmptyIsAnyOf,
    valueFormatter: defaultValueFormatter
  },
  {
    field: 'siteName',
    headerName: 'Site',
    flex: 1,
    minWidth: 200,
    valueGetter: (value, row) => (!row.siteId && !row.siteName ? 'General Interest' : row.siteName),
    valueFormatter: (value, row) => {
      const { siteId, siteName } = row;
      return !siteId && !siteName ? 'General Interest' : siteName;
    }
  },
  {
    field: 'email',
    headerName: 'Email',
    flex: 1,
    minWidth: 180,
    filterOperators: containsIsEmptyIsNotEmpty
  },
  {
    field: 'phoneNumber',
    headerName: 'Phone',
    flex: 1,
    minWidth: 140,
    filterOperators: containsIsEmptyIsNotEmpty
  },
  {
    field: 'source',
    headerName: 'Source',
    flex: 0.7,
    minWidth: 120,
    filterOperators: allStringOperatorsExceptIsEmptyAndIsNotEmpty
  },
  {
    field: 'patientLeadStatus',
    headerName: 'Patient Lead Status',
    flex: 1,
    minWidth: 170,
    editable: true,
    type: 'singleSelect',
    valueOptions: PATIENT_LEAD_STATUS_OPTIONS,
    filterOperators: singleSelectIsIsAnyOf,
    valueGetter: (value, row) => (row.source === WEBFORM_SOURCE_NAME ? value ?? '' : ''),
    renderEditCell: params => <PatientLeadStatusEditCell {...params} />
  },
  {
    field: 'sitePatientStatus',
    headerName: 'Patient Status',
    flex: 1,
    minWidth: 140,
    type: 'singleSelect',
    valueOptions: getPatientStatusNames(),
    filterOperators: singleSelectIsIsNotIsAnyOf
  },
  {
    field: 'medicalHistories',
    headerName: 'Medical History',
    flex: 1,
    minWidth: 180,
    valueGetter: value => formatArrayForGrid(value),
    valueFormatter: value => formatArrayForGrid(value)
  },
  {
    field: 'medications',
    headerName: 'Concomitant Medications',
    flex: 1,
    minWidth: 200,
    valueGetter: value => formatArrayForGrid(value),
    valueFormatter: value => formatArrayForGrid(value)
  },
  {
    field: 'communicationOption',
    headerName: 'Additional Communication',
    flex: 1,
    minWidth: 200,
    type: 'singleSelect',
    valueOptions: ADDITIONAL_COMMUNICATION_OPTIONS,
    filterOperators: singleSelectIs,
    valueGetter: value => (value ? OPT_IN : OPT_OUT)
  },
  {
    field: 'smsOptIn',
    headerName: 'SMS Opt-In',
    flex: 0.8,
    minWidth: 120,
    type: 'singleSelect',
    valueOptions: ADDITIONAL_COMMUNICATION_OPTIONS,
    filterOperators: singleSelectIs,
    valueGetter: value => (value ? OPT_IN : OPT_OUT)
  },
  {
    field: 'createdAt',
    headerName: 'Creation Date',
    flex: 1,
    minWidth: 140,
    type: 'date',
    valueGetter: value => (value ? new Date(value) : null),
    valueFormatter: dateValueFormatter
  },
  {
    field: 'updatedAt',
    headerName: 'Last Updated',
    flex: 1,
    minWidth: 140,
    type: 'date',
    valueGetter: (value, row) => {
      if (row?.sitePatientId) {
        return row.sitePatientUpdateDate ? new Date(row.sitePatientUpdateDate) : null;
      }
      if (row?.id) {
        return row.updatedAt ? new Date(row.updatedAt) : null;
      }
      return value ? new Date(value) : null;
    },
    valueFormatter: (value, row) => (row ? updateDateFormatResolver(row) : dateValueFormatter(value))
  }
];

function updateDateFormatResolver(row) {
  if (row.sitePatientId) {
    return row.sitePatientUpdateDate ? dateValueFormatter(row.sitePatientUpdateDate) : null;
  }
  if (row.id) {
    return row.updatedAt ? dateValueFormatter(row.updatedAt) : null;
  }
  return '';
}

function defaultValueFormatter(value) {
  return value ? value : '';
}
